import React from 'react';
import gql from 'graphql-tag';
import { graphql } from 'react-apollo';
import {
    ExpansionPanel,
    ExpansionPanelSummary,
    ExpansionPanelDetails,
    Typography,
    CircularProgress
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import MediaList from '../../components/MediaList/MediaList';
import MediaPlayerDialog from '../../components/MediaPlayer/MediaPlayerDialog';
import useMediaOpener from '../../components/MediaPlayer/useMediaOpener';
import { groupByYearAndSemester, extractWorkbookMedias } from './groupExtraActivities';

const FETCH_EXTRA_ACTIVITIES = gql`
    query fetchExtraActivities($courseUnitId: Int){
        fetchExtraActivities(courseUnitId: $courseUnitId) {
            id
            name
            years {
                year
                semesters {
                    semester
                    label
                    medias {
                        id
                        type
                        name
                        description
                        link
                    }
                }
            }
        }
    }
`;

// ExpansionPanelDetails é flex em linha por padrão; aqui o conteúdo é uma pilha.
const stackedDetails = { display: 'block', padding: 0 };

const Section = ({ title, defaultExpanded, nested, children }) => (
    <ExpansionPanel
        defaultExpanded={defaultExpanded}
        elevation={nested ? 0 : 1}
        style={nested ? { borderTop: '1px solid rgba(0, 0, 0, 0.12)' } : undefined}
    >
        <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant={nested ? 'body1' : 'subtitle1'} style={{ fontWeight: nested ? 400 : 500 }}>
                {title}
            </Typography>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails style={stackedDetails}>
            {children}
        </ExpansionPanelDetails>
    </ExpansionPanel>
);

const TypeGroup = ({ typeGroup, onClickMedia }) => (
    <>
        <Typography
            variant="overline"
            color="primary"
            component="p"
            style={{ padding: '8px 16px 0', fontWeight: 500 }}
        >
            {typeGroup.activityTypeName}
        </Typography>
        <MediaList medias={typeGroup.medias} onClickMedia={onClickMedia} />
    </>
);

const Message = ({ children }) => (
    <Typography color="textSecondary" align="center" style={{ padding: '48px 16px' }}>
        {children}
    </Typography>
);

const ExtraActivities = props => {

    let { loading, error, fetchExtraActivities } = props.data;

    const { playingMedia, openMedia, closeMedia } = useMediaOpener();

    if (loading)
        return (
            <div style={{ textAlign: 'center', padding: 48 }}>
                <CircularProgress aria-label="Carregando atividades extras" />
            </div>
        );

    if (error) return <Message>Não foi possível carregar as atividades extras. Tente novamente mais tarde.</Message>;

    const workbookMedias = extractWorkbookMedias(fetchExtraActivities);
    const years = groupByYearAndSemester(fetchExtraActivities);

    if (workbookMedias.length === 0 && years.length === 0)
        return <Message>Nenhuma atividade extra disponível para o livro selecionado.</Message>;

    return (
        // key: remonta ao trocar de livro para reaplicar o defaultExpanded.
        <div key={props.courseUnitId}>
            {workbookMedias.length > 0 && (
                <Section title="Workbook" defaultExpanded>
                    <MediaList medias={workbookMedias} onClickMedia={openMedia} />
                </Section>
            )}

            {years.map((year, yearIndex) => (
                <Section key={year.year} title={year.year} defaultExpanded={workbookMedias.length === 0 && yearIndex === 0}>
                    {year.semesters.map((semester, semesterIndex) => (
                        <Section key={semester.semester} title={semester.label} defaultExpanded={semesterIndex === 0} nested>
                            {semester.typeGroups.length === 0 && (
                                <Typography color="textSecondary" variant="body2" style={{ padding: 16 }}>
                                    Nenhum conteúdo neste semestre.
                                </Typography>
                            )}
                            {semester.typeGroups.map(typeGroup => (
                                <TypeGroup key={typeGroup.activityTypeName} typeGroup={typeGroup} onClickMedia={openMedia} />
                            ))}
                        </Section>
                    ))}
                </Section>
            ))}

            <MediaPlayerDialog media={playingMedia} onClose={closeMedia} />
        </div>
    );
}

export default graphql(FETCH_EXTRA_ACTIVITIES, {
    options: props => ({
        variables: {
            courseUnitId: props.courseUnitId
        }
    })
})(ExtraActivities);
